import {
  buildClientMetadata,
  submitWidgetFeedback,
  uploadAttachmentDataUrl,
  type ShellHydration,
} from '../../../api.js';
import type { Attachment, FeedbackInline, WidgetRatingStyle } from '../types.js';
import { defaultAttachmentFilename } from '../utils/layout.js';

interface SubmissionInput {
  hydration: ShellHydration;
  attachments: Attachment[];
  rating: number | null;
  comment: string;
  fullName: string;
  email: string;
  selectedType: string | null;
  inline: FeedbackInline | null;
  ratingStyle?: WidgetRatingStyle;
}

function widgetStyleToSdkStyle(style: WidgetRatingStyle | undefined): string {
  if (!style) return 'rating';
  if (style === 'thumbs') return 'thumbs';
  if (style === 'nps-0-10') return 'nps';
  if (style === 'emoji-5') return 'emoji';
  return 'stars';
}

interface SubmissionOutcome {
  ok: boolean;
  persistedCount: number;
  failedCount: number;
  submittedType: string;
}

export async function performFeedbackSubmission(input: SubmissionInput): Promise<SubmissionOutcome> {
  const uploaded = await Promise.all(
    input.attachments.map((attachment) => {
      const filename = attachment.filename ?? defaultAttachmentFilename(attachment);
      return uploadAttachmentDataUrl(input.hydration, attachment.url, filename, attachment.kind);
    }),
  );
  const persisted = uploaded.filter((entry): entry is NonNullable<typeof entry> => Boolean(entry && entry.key));
  const failedCount = uploaded.length - persisted.length;
  const submittedType = input.selectedType ?? input.inline?.type ?? 'global';

  const client = buildClientMetadata(input.hydration.host?.url);
  const ok = await submitWidgetFeedback(input.hydration, {
    feedbackType: submittedType,
    rating: input.rating ?? undefined,
    ratingStyle: widgetStyleToSdkStyle(input.ratingStyle),
    comment: input.comment,
    // `name` is the event-style grouping key from Chirp.feedback.open({ name }).
    name: input.inline?.name ?? 'global',
    // Submitter identity is kept separate from the feedback name.
    userName: input.fullName || undefined,
    email: input.email || undefined,
    attachments: persisted,
    metadata: {
      source: 'embed-shell',
      name: input.inline?.name ?? 'global',
      category: input.inline?.category,
      browser: client.browser,
      operatingSystem: client.operatingSystem,
      os: client.os,
      device: client.device,
      currentDomain: client.currentDomain,
      currentURL: client.currentURL,
      screenWidth: client.screenWidth,
      screenHeight: client.screenHeight,
      userAgent: client.userAgent,
      language: client.language,
      timezone: client.timezone,
      referrer: input.hydration.host?.referrer,
      ...(input.inline?.metadata ?? {}),
    },
  });

  return { ok, persistedCount: persisted.length, failedCount, submittedType };
}
